import ClearIcon from '@mui/icons-material/Clear';
import { FormControl, IconButton, InputAdornment, TextField } from '@mui/material';
import FieldInfo from '../../field-info';
import Widget from '../widget';
import type { SingleLineTextWidgetHookProps, SingleLineTextWidgetOwnProps } from './single-line-text-hook';

export type SingleLineTextCombinedProps = SingleLineTextWidgetOwnProps & SingleLineTextWidgetHookProps;

export type SingleLineTextInputProps = Pick<
  SingleLineTextCombinedProps,
  | 'value'
  | 'error'
  | 'isRequired'
  | 'multiline'
  | 'rows'
  | 'minRows'
  | 'maxRows'
  | 'disabled'
  | 'onBlur'
  | 'onFocus'
  | 'handleChange'
  | 'clearable'
  | 'handleClear'
  | 'size'
  | 'readOnly'
  | 'id'
  | 'label'
  | 'description'
  | 'instructions'
  | 'maxLength'
  | 'endAdornment'
>;

export function SingleLineTextInput({
  value,
  error,
  isRequired,
  multiline,
  rows,
  minRows,
  maxRows,
  disabled,
  onBlur,
  onFocus,
  handleChange,
  clearable,
  handleClear,
  size,
  readOnly,
  id,
  label,
  description,
  instructions,
  maxLength,
  endAdornment
}: SingleLineTextInputProps) {
  endAdornment ||= clearable ? (
    <InputAdornment position="end">
      <IconButton title="Clear field content" onClick={handleClear} size="large">
        <ClearIcon />
      </IconButton>
    </InputAdornment>
  ) : null;

  return (
    <FormControl fullWidth variant="outlined" error={Boolean(error)}>
      <TextField
        fullWidth
        size={size ?? 'small'}
        variant="outlined"
        type="text"
        label={label}
        value={value}
        disabled={Boolean(disabled) || Boolean(readOnly)}
        required={isRequired}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        inputProps={{
          maxLength,
          'data-testid': `${id}__input`
        }}
        InputProps={{
          endAdornment
        }}
      />
      <FieldInfo description={description} instructions={instructions} error={error} />
    </FormControl>
  );
}

export function SingleLineTextWidget(props: SingleLineTextCombinedProps) {
  return (
    <Widget testId={props.id}>
      <SingleLineTextInput {...props} />
    </Widget>
  );
}
